import { useEffect } from "react";

const TEXT_INPUT_TAGS = new Set(["input", "textarea", "select"]);

function isTextEntryTarget(target) {
  if (!target) return false;
  const tag = (target.tagName || "").toLowerCase();
  if (TEXT_INPUT_TAGS.has(tag)) return true;
  if (target.isContentEditable) return true;
  return false;
}

// ---------------------------------------------------------------------------
// editorKit/useKeyboardManipulation — toutes les interactions clavier de
// manipulation d'éléments pour un éditeur visuel, dans un seul hook.
//
// Toujours inerte quand le focus est dans un champ de saisie (input,
// textarea, select, contentEditable) — les flèches continuent alors à
// déplacer le curseur texte normalement, comme l'exigeait déjà le seul
// listener existant (le nudge clavier en mode libre de PageBuilder.jsx).
//
// Raccourcis (voir le plan pour la justification de chaque choix — pensés
// pour ne pas entrer en conflit avec les raccourcis navigateur/OS réservés
// Alt+Flèche = précédent/suivant, Ctrl/Cmd+Flèche = Mission Control sur Mac) :
//   Déplacer            Flèche (1px) / Maj+Flèche (10px)
//   Redimensionner       Alt+Maj+Flèche (1px) / Alt+Maj+Ctrl(Cmd)+Flèche (10px)
//   Réordonner (fratrie) Ctrl/Cmd+Maj+Flèche Haut/Bas
//   Reparenter           Ctrl/Cmd+Maj+Flèche Gauche (outdent) / Droite (indent)
//   Supprimer            Suppr / Retour arrière
//   Dupliquer            Ctrl/Cmd+D
//   Copier / Coller      Ctrl/Cmd+C / Ctrl/Cmd+V
//   Annuler / Rétablir   Ctrl/Cmd+Z / Ctrl/Cmd+Maj+Z (ou Ctrl/Cmd+Y)
//   Désélectionner       Échap
// ---------------------------------------------------------------------------
export function useKeyboardManipulation({
  enabled = true,
  hasSelection = false,
  onMove, onResize, onReorder, onReparent,
  onDelete, onDuplicate, onCopy, onPaste,
  onUndo, onRedo, onEscape,
} = {}) {
  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (e) => {
      if (isTextEntryTarget(e.target)) return;

      const meta = e.ctrlKey || e.metaKey;

      // Annuler/rétablir — actifs même sans sélection.
      if (meta && !e.altKey && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) onRedo?.(); else onUndo?.();
        return;
      }
      if (meta && !e.altKey && !e.shiftKey && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        onRedo?.();
        return;
      }

      if (!hasSelection) return;

      if (e.key === "Escape") { onEscape?.(); return; }

      if ((e.key === "Delete" || e.key === "Backspace") && !meta && !e.altKey) {
        e.preventDefault();
        onDelete?.();
        return;
      }
      if (meta && !e.shiftKey && !e.altKey && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        onDuplicate?.();
        return;
      }
      if (meta && !e.shiftKey && !e.altKey && (e.key === "c" || e.key === "C") && onCopy) {
        onCopy();
        return;
      }
      if (meta && !e.shiftKey && !e.altKey && (e.key === "v" || e.key === "V") && onPaste) {
        onPaste();
        return;
      }

      const dir = {
        ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0],
      }[e.key];
      if (!dir) return;

      // Redimensionner : Alt+Maj(+Ctrl/Cmd)+Flèche
      if (e.altKey && e.shiftKey) {
        if (!onResize) return;
        e.preventDefault();
        const step = meta ? 10 : 1;
        onResize(dir[0] * step, dir[1] * step);
        return;
      }
      // Réordonner (Haut/Bas) ou reparenter (Gauche/Droite) : Ctrl/Cmd+Maj+Flèche
      if (meta && e.shiftKey && !e.altKey) {
        const isVertical = e.key === "ArrowUp" || e.key === "ArrowDown";
        if (isVertical ? !onReorder : !onReparent) return;
        e.preventDefault();
        if (isVertical) onReorder(e.key === "ArrowUp" ? -1 : 1);
        else onReparent(e.key === "ArrowLeft" ? "outdent" : "indent");
        return;
      }
      // Déplacer : Flèche / Maj+Flèche
      if (!meta && !e.altKey) {
        if (!onMove) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        onMove(dir[0] * step, dir[1] * step);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    enabled, hasSelection, onMove, onResize, onReorder, onReparent,
    onDelete, onDuplicate, onCopy, onPaste, onUndo, onRedo, onEscape,
  ]);
}
